import { Db } from '@cloudbase/database'
import { ICloudbase } from '@cloudbase/types'
import { ICloudbaseComponent } from '@cloudbase/types/component'
import cloudbaseNS from '../../index'
import { EJSON } from 'bson'

declare const cloudbase: ICloudbase
declare type QueryType = 'WHERE' | 'DOC'

/**
 * 数据库配置接口
 */
interface IDbConfig {
  /** 实例 ID（可选，默认 '(default)'） */
  instance?: string
  /** 数据库名称（可选，默认 '(default)'） */
  database?: string
}

interface IBaseReq extends IDbConfig {
  collectionName: string
  transactionId?: string
}

interface IDocumentsQueryReq extends IBaseReq {
  query?: any
  /** @deprecated 使用 query 替代 */
  queryType?: QueryType
  order?: string[]
  offset?: number
  limit?: number
  projection?: Object
}

interface IDocumentsAddReq extends IBaseReq {
  data: Object | Object[]
}

interface IDocumentsUpdateReq extends IBaseReq {
  query: Object
  data: Object
  /** 是否批量更新（默认 false） */
  multi?: boolean
  /** 把所有更新数据转为带操作符的 */
  merge?: boolean
  /** 不存在时是否创建（默认 false） */
  upsert?: boolean
  /** 是否替换整个文档（默认 false，为 true 时替换整个文档而非合并更新） */
  replaceMode?: boolean
  /** @deprecated 使用 query 替代 */
  queryType?: QueryType
}

interface IDocumentsRemoveReq extends IBaseReq {
  query: Object
  /** 是否批量删除（默认 false） */
  multi?: boolean
  /** @deprecated 使用 query 替代 */
  queryType?: QueryType
}

interface IDocumentsCountReq extends IBaseReq {
  query: Object
  /** @deprecated 使用 query 替代 */
  queryType?: QueryType
}

interface ICommonRes<T> {
  data?: T & { code?: string }
  requestId: string
  code?: string
  message?: string
}

interface IAddRes {
  /** 单条插入时返回的文档 ID（批量插入时不返回） */
  _id?: string
  /** 批量插入时返回的文档 ID 列表 */
  ids?: string[]
  insertedIds: string[]
}

interface IRunCommandsReq {
  transactionId?: string
  /** 命令数组，方法会将其转为 MongoDB 原生命令（EJSON 对象格式） */
  commands: Object[]
}

const COMPONENT_NAME = 'database'

const gateWayFunc = {
  /**
   * 过滤响应，移除不必要的字段
   */
  filterRes <T>(res: ICommonRes<T> & { header?: any; statusCode?: number }): ICommonRes<T> {
    delete res.header
    delete res.statusCode
    return res
  },

  /**
   * 展开响应数据到顶层（用于事务等场景）
   */
  flattenResData <T>(res: ICommonRes<T>): any {
    if (typeof res.data === 'object' && res.data !== null) {
      const result = { ...res, ...res.data }
      delete result.data
      return result
    }
    return res
  },

  /**
   * 安全解析 EJSON
   */
  safeParseEJSON(data: any): any {
    if (typeof data !== 'string') return data
    try {
      return EJSON.parse(data)
    } catch {
      return data
    }
  },

  /**
   * 构建 URL 查询参数
   */
  buildQueryParams(params: Record<string, any>): string {
    return Object.entries(params)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
        return `${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`
      })
      .join('&')
  },

  /**
   * 获取 URL 前缀
   */
  getUrlPrefix(data: IDbConfig): string {
    const instance = data?.instance || '(default)'
    const database = data?.database || '(default)'
    return `/database/instances/${instance}/databases/${database}`
  },

  async 'database.queryDocument'(data: IDocumentsQueryReq,): Promise<ICommonRes<{ offset?: number; limit?: number; list: any[] }>> {
    const isQueryDoc = !!data.query?._id?.$eq
    const queryParams = gateWayFunc.buildQueryParams({
      projection: data.projection,
      transactionId: data.transactionId,
      ...(isQueryDoc
        ? {}
        : {
          offset: data.offset ?? 0,
          limit: data.limit ?? 100,
          order: data.order,
          query: data.query ? EJSON.stringify(data.query, { relaxed: false }) : undefined,
        }),
    })

    let url = `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents?${queryParams}`

    // 查询单个文档
    if (isQueryDoc) {
      url = `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents/${
        data.query._id?.$eq || data.query._id
      }?${queryParams}`
    }

    let res = await this.gateWay({
      url,
      method: 'GET',
    })

    // 尝试解析 EJSON 格式的数据
    res.data = gateWayFunc.safeParseEJSON(JSON.stringify(res.data))

    if (isQueryDoc && !res.code && !res.data?.code) {
      return {
        ...res,
        data: { list: [res.data] }
      }
    }

    // 确保 res.data.list 为数组，兼容后端返回数据或直接返回文档数组的情况
    if (!res.code && !res.data?.code) {
      if (Array.isArray(res.data)) {
        // 后端直接返回文档数组
        res = { ...res, data: { list: res.data } }
      } else if (res.data && !Array.isArray(res.data.list)) {
        // 后端返回对象但缺少 list 字段
        res = { ...res, data: { ...res.data, list: [] } }
      }
    } else {
      res = { ...res, code: res.code || res.data?.code, data: { ...res.data, list: [] } }
    }

    return gateWayFunc.filterRes(res)
  },

  async 'database.addDocument'(data: IDocumentsAddReq): Promise<ICommonRes<IAddRes>> {
    let res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents`,
      method: 'POST',
      data: {
        data: Array.isArray(data.data) ? data.data : [data.data],
        ...(data.transactionId && { transactionId: data.transactionId }),
      },
    })

    if (Array.isArray(data.data)) {
      // 数组模式（批量插入），返回 ids 列表
      res = {
        ...res,
        data: {
          ids: res.data?.insertedIds || [],
          ...res.data,
        },
      }
    } else if (res.data?.insertedIds?.[0]) {
      // 非数组模式（单条插入），将 _id 提升到顶层
      res = {
        ...res,
        data: {
          _id: res.data.insertedIds[0],
          ...res.data,
        },
      }
    }

    return gateWayFunc.filterRes(res)
  },

  async 'database.updateDocument'(data: IDocumentsUpdateReq,): Promise<ICommonRes<{ matched: number; updated: number; upsert_id: string }>> {
    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents`,
      method: 'PATCH',
      data: {
        query: data.query,
        data: data.data,
        multi: !!data.multi,
        upsert: !!data.upsert,
        replaceMode: !!data.replaceMode,
        ...(data.transactionId && { transactionId: data.transactionId }),
      },
    })

    return gateWayFunc.filterRes(res)
  },

  async 'database.deleteDocument'(data: IDocumentsRemoveReq): Promise<ICommonRes<{ deleted: number }>> {
    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents/remove`,
      method: 'POST',
      data: {
        query: data.query,
        multi: !!data.multi,
        ...(data.transactionId && { transactionId: data.transactionId }),
      },
    })

    return gateWayFunc.filterRes(res)
  },

  async 'database.countDocument'(data: IDocumentsCountReq): Promise<ICommonRes<{ total: number }>> {
    const queryParams = gateWayFunc.buildQueryParams({
      count: true,
      query: data.query ? EJSON.stringify(data.query, { relaxed: false }) : undefined,
    })

    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents?${queryParams}`,
      method: 'GET',
    })

    return gateWayFunc.filterRes(res)
  },

  async 'database.addCollection'(data: { collectionName: string } & IDbConfig): Promise<ICommonRes<''>> {
    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/collections`,
      method: 'POST',
      data: { collectionName: data.collectionName },
    })

    return gateWayFunc.filterRes(res)
  },

  async 'database.aggregate'(data: { collectionName: string; stages: { stageKey: string; stageValue: any }[] } & IDbConfig,): Promise<ICommonRes<{ list: string }>> {
    const pipeline = data.stages.map((stage) => {
      try {
        // stageValue 可能是字符串或对象，统一处理
        const value = typeof stage.stageValue === 'string' ? JSON.parse(stage.stageValue) : stage.stageValue
        return {
          [stage.stageKey]: EJSON.serialize(value),
        }
      } catch {
        return { [stage.stageKey]: stage.stageValue }
      }
    })

    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/collections/${data.collectionName}/documents/aggregations`,
      method: 'POST',
      data: { pipeline },
    })

    // 将结果列表转为字符串格式（兼容原有逻辑）
    if (Array.isArray(res.data?.list)) {
      res.data.list = JSON.stringify(res.data.list.map((item: any) => JSON.stringify(item)))
    }

    return gateWayFunc.filterRes(res)
  },

  async 'database.startTransaction'(data: IDbConfig) {
    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/transactions`,
      method: 'POST',
    })

    return gateWayFunc.filterRes(gateWayFunc.flattenResData(res))
  },

  async 'database.commitTransaction'(data: { transactionId: string } & IDbConfig) {
    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/transactions/${data.transactionId}/commit`,
      method: 'POST',
    })

    return gateWayFunc.filterRes(res)
  },

  async 'database.abortTransaction'(data: { transactionId: string } & IDbConfig) {
    const res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/transactions/${data.transactionId}/rollback`,
      method: 'POST',
    })

    return gateWayFunc.filterRes(res)
  },

  async 'database.getInTransaction'(data: IDocumentsQueryReq) {
    const res = await gateWayFunc['database.queryDocument'].call(this, data)

    if (typeof res.data === 'object' && res.data !== null) {
      if (res.data?.code) {
        return gateWayFunc.flattenResData(res)
      }
      return { ...res, data: JSON.stringify(res.data) as any }
    }

    return res
  },

  async 'database.updateDocInTransaction'(data: IDocumentsUpdateReq) {
    // 尝试解析 EJSON 格式的数据
    data.data = gateWayFunc.safeParseEJSON(data.data)

    const res = await gateWayFunc['database.updateDocument'].call(this, data)
    return gateWayFunc.flattenResData(res)
  },

  async 'database.deleteDocInTransaction'(data: IDocumentsRemoveReq) {
    const res = await gateWayFunc['database.deleteDocument'].call(this, data)
    return gateWayFunc.flattenResData(res)
  },

  async 'database.insertDocInTransaction'(data: IDocumentsAddReq,): Promise<ICommonRes<IAddRes & { inserted?: number }>> {
    // 尝试解析 EJSON 格式的数据
    data.data = gateWayFunc.safeParseEJSON(data.data)

    let res = await gateWayFunc['database.addDocument'].call(this, data)

    if (typeof res.data === 'object' && res.data !== null) {
      const hasId = !!res.data?._id
      res = {
        ...res,
        inserted: hasId ? 1 : 0,
        ok: hasId ? 1 : 0,
        ...res.data,
      } as any
    } else {
      res = { inserted: 0, ok: 0, ...res } as any
    }

    delete res.data

    return res
  },

  async 'database.runCommands'(data: IRunCommandsReq & IDbConfig,): Promise<ICommonRes<{ list: Object[][] }>> {
    let res = await this.gateWay({
      url: `${gateWayFunc.getUrlPrefix(data)}/commands`,
      method: 'POST',
      data: {
        commands: EJSON.serialize(data.commands),
        ...(data.transactionId && { transactionId: data.transactionId }),
      },
    })

    if (typeof res.data === 'object' && res.data !== null) {
      res = { ...res, ...res.data }
      delete res.data
      delete res.request_id
    }

    // 尝试将结果转换为普通对象
    try {
      res = EJSON.deserialize(res)
    } catch {
      // 忽略转换失败
    }

    return gateWayFunc.filterRes(res)
  },
}

function database(dbConfig?: IDbConfig) {
  const { adapter, runtime } = this.platform

  class Req extends this.request.constructor {
    constructor(config) {
      super(config)
    }

    async send(action: string, data: any) {
      let res = null

      if (this.config.endPointMode === 'GATEWAY' && gateWayFunc[action]) {
        res = await gateWayFunc[action].call(this, data)
      } else {
        res = await super.send(action, data)
      }

      return res
    }
  }

  Db.reqClass = Req
  // 未登录情况下传入空函数
  Db.getAccessToken = this.authInstance ? this.authInstance.getAccessToken.bind(this.authInstance) : () => ''
  Db.runtime = runtime
  if (this.wsClientClass) {
    Db.wsClass = adapter.wsClass
    Db.wsClientClass = this.wsClientClass
  }

  if (!Db.ws) {
    Db.ws = null
  }

  class NewDb extends Db {
    constructor(config?: IDbConfig) {
      super(config)
    }

    async runCommands(params: IRunCommandsReq): Promise<ICommonRes<{ list: Object[][] }>> {
      const request = Db.createRequest(this.config)

      return request.send('database.runCommands', params)
    }
  }

  return new NewDb({ ...this.config, _fromApp: this, ...dbConfig })
}

const component: ICloudbaseComponent = {
  name: COMPONENT_NAME,
  entity: {
    database,
  },
}
try {
  cloudbase.registerComponent(component)
} catch (e) {}

export function registerDatabase(app: ICloudbase | typeof cloudbaseNS) {
  try {
    app.registerComponent(component)
  } catch (e) {
    console.warn(e)
  }
}

try {
  (window as any).registerDatabase = registerDatabase
} catch (e) {}
